"use client";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControl,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  getUserPersonalInfo,
  editProfileDataAPI,
} from "../../../../api/userApi";
import { useSubscriptionStyles } from "./style";
import { useSubscriptionPlansQuery } from "../../../../query/hooks/useSubscriptionPlansQuery";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { ReactComponent as IconCheckFill } from "../../../../assets/images/icons/iconCheckFill.svg"
import { ReactComponent as IconCheckbox } from "../../../../assets/images/icons/iconCheckbox.svg"
import { ReactComponent as UploadImageIcon } from "../../../../assets/images/icons/uploadImageIcon.svg"
import { Plus } from "lucide-react";
import { Close } from "@material-ui/icons";
import WelcomeUploadDialog from "./WelcomeUploadDialog";

type PlansType = { id: string, name: string }

type WelcomeMessage = {
  _id: string;
  text: string;
  plans: string[];
  images?: (File | string)[];
  isEditing?: boolean;
};

const WelcomeMessages: React.FC = () => {
  const classes = useSubscriptionStyles();
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [welcomeMessages, setWelcomeMessages] = useState<WelcomeMessage[]>([]);
  const [uploadDialog, setUploadDialog] = useState(false);

  const { data: plansData, isLoading: plansLoading } = useSubscriptionPlansQuery();
  const plans = useMemo(() => {
    const items = (plansData as any)?.items ?? (Array.isArray(plansData) ? plansData : []);
    return items.map((plan: any) => ({
      id: plan._id,
      name: plan.name,
    }));
  }, [plansData]);

  const allSelectedPlans = welcomeMessages.flatMap(msg => msg.plans);
  const uniqueSelectedPlans = new Set(allSelectedPlans);
  const allPlansUsed = uniqueSelectedPlans.size === plans.length;

  const toggleEdit = (index: number, editing: boolean) => {
    const newMessages = [...welcomeMessages];

    if (!editing) {
      const msg = newMessages[index];
      if (!msg.text.trim() && msg.plans.length === 0) {
        // if message empty - delete
        newMessages.splice(index, 1);
      } else {
        newMessages[index].isEditing = false;
      }
    } else {
      newMessages[index].isEditing = true;
    }

    setWelcomeMessages(newMessages);
  };

  const handlePlanChange = (index: number, event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string[];
    const newMessages = [...welcomeMessages];

    if (value.includes("all")) {
      newMessages[index].plans =
        newMessages[index].plans.length !== plans.length
          ? plans.map((p: PlansType) => p.name)
          : [];
    } else {
      newMessages[index].plans = value;
    }

    setWelcomeMessages(newMessages);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getUserPersonalInfo();
        const messages = (res.data?.welcomeMessage || []).map((msg: any) => ({
          ...msg,
          text: msg.text ? msg.text : msg[0],
          isEditing: false,
        }));
        setWelcomeMessages(messages || []);
      } catch (error) {
        toast.error("Failed to load welcome message");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const reloadWelcomeMessages = async () => {
    const res = await getUserPersonalInfo();
    const messages = (res.data?.welcomeMessage || []).map((msg: any) => ({
      ...msg,
      text: msg.text ? msg.text : msg[0],
      isEditing: false,
    }));
    setWelcomeMessages(messages || []);
  };

  const handleSave = async (index: number) => {
    if (savingIndex !== null) return;

    const msg = welcomeMessages[index];

    if (!msg.text || !msg.text.trim()) {
      return toast.error("Message cannot be empty");
    }
    if (msg.text.trim().length > 200) {
      return toast.error("Message cannot exceed 200 characters");
    }
    const payload = {
      index,
      message: {
        text: msg.text,
        plans: msg.plans,
        images: (msg.images || []).filter(
          (item): item is string => typeof item === "string",
        ),
      }
    };
    const formData = new FormData();
    formData.append("welcomeMessage", JSON.stringify(payload));

    if (msg.images && msg.images.length > 0) {
      msg.images.forEach((media) => {
        if (media instanceof File) {
          formData.append("welcomeImages", media);
        }
      });
    }
    try {
      setSavingIndex(index);
      await editProfileDataAPI(formData);
      await reloadWelcomeMessages();
      toast.success("Welcome message saved successfully");
    } catch (error) {
      toast.error("Failed to save welcome message");
      console.error(error);
    } finally {
      setSavingIndex(null);
    }
  };

  const uploadFiles = (
    files: File[],
    remainingMediaUrls: string[],
    index: number,
  ) => {
    const newMessages = [...welcomeMessages];
    newMessages[index].images = [...remainingMediaUrls, ...files];
    setWelcomeMessages(newMessages);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={28} style={{ color: "#FE5A97" }} />
      </Box>
    );
  }

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>Welcome Message</Typography>
      <Typography className={classes.infoText}>
        Send an automatic message to new subscribers right after they join
      </Typography>

      {welcomeMessages && welcomeMessages.length > 0 && welcomeMessages.map((welcomeMessage, index) => {
        const usedPlans = welcomeMessages
          .filter((_, i) => i !== index)
          .flatMap(msg => msg.plans);
        return welcomeMessage.isEditing ?
          (
            <Card key={welcomeMessage._id} className={classes.tierCard}>
              <CardContent className={classes.cardContent}>
                <Box>
                  <Box className={classes.tierHeader}>
                    <Typography className={classes.tierName}>Add Message</Typography>
                  </Box>
                  <Box position="relative">
                    <TextField
                      className={classes.inputField}
                      variant="outlined"
                      multiline
                      fullWidth
                      minRows={2}
                      placeholder="Say “Hi” to your new subscribers"
                      value={welcomeMessage.text}
                      onChange={(e) => {
                        const newMessages = [...welcomeMessages];
                        newMessages[index].text = e.target.value;
                        setWelcomeMessages(newMessages);
                      }}
                      InputProps={{
                        style: {
                          fontSize: "16px",
                          padding: "14px",
                        },
                      }}
                    />
                    {/* Upload image */}
                    <Box style={{ position: "absolute", top: 8, right: 8, cursor: "pointer" }}>
                      <UploadImageIcon onClick={() => setUploadDialog(true)} />
                    </Box>
                  </Box>
                  {welcomeMessage.text ? <Typography variant="caption" style={{ marginTop: 8 }} className={classes.infoText}>
                    {welcomeMessage.text.length}/200 characters
                  </Typography> : ""}
                </Box>

                <Box>
                  <Typography variant="caption">Subscription type</Typography>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      multiple
                      value={welcomeMessages[index].plans}
                      onChange={e => handlePlanChange(index, e)}
                      className={classes.subscriptionSelect}
                      renderValue={(selected) => {
                        const values = selected as string[];
                        if (values.length === plans.length) {
                          return <span style={{ color: "#FFFFFF" }}>All subscriptions</span>;
                        }
                        return (
                          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                            {values.map((value) => (
                              <Chip key={value} label={value} className={classes.subscriptionChip}
                                onDelete={() => {
                                  const newMessages = [...welcomeMessages];
                                  newMessages[index].plans = newMessages[index].plans.filter(
                                    (plan) => plan !== value
                                  );
                                  setWelcomeMessages(newMessages);
                                }}
                                deleteIcon={<Close style={{ color: "#fff" }} />}
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            ))}
                          </Box>
                        );
                      }}
                      IconComponent={(props) => <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20, marginRight: 12 }} />}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        classes: { paper: classes.menuPaper },
                      }}
                    >

                      {plansLoading &&
                        <CircularProgress
                          size={20}
                          style={{ color: "#ff69b4" }}
                        />}


                      {/* "All subscriptions" */}
                      <MenuItem disableGutters value="all" style={{ borderBottom: "1px solid #1F1F1F", padding: 0 }}>
                        <Checkbox
                          checked={welcomeMessages[index].plans.length === plans.length}
                          indeterminate={
                            welcomeMessages[index].plans.length > 0 &&
                            welcomeMessages[index].plans.length < plans.length
                          }
                          icon={<IconCheckbox />}
                          checkedIcon={<IconCheckFill />}
                          indeterminateIcon={<IconCheckbox />}
                        />
                        <ListItemText primary="All subscriptions"
                          primaryTypographyProps={{
                            style: { fontSize: "16px", },
                          }}
                        />
                      </MenuItem>

                      {plans.map((plan: any) => {
                        const disabled = usedPlans.includes(plan.name);

                        return (
                          <MenuItem disableGutters key={plan.id}
                            disabled={disabled}
                            value={plan.name}
                            style={{ marginLeft: 12, padding: 0 }}>
                            <Checkbox
                              checked={welcomeMessages[index].plans.includes(plan.name)}
                              icon={<IconCheckbox />}
                              checkedIcon={<IconCheckFill />}
                            />
                            <ListItemText primary={plan.name}
                              primaryTypographyProps={{
                                style: { fontSize: "16px", },
                              }}
                            />
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
              <Box style={{ display: "flex", justifyContent: "flex-end", gap: 20, marginTop: 24 }}>
                <Button
                  className={classes.cancelButton}
                  onClick={() => toggleEdit(index, false)}
                  disabled={savingIndex === index}
                >
                  Cancel
                </Button>
                <Button
                  className={classes.saveButton}
                  onClick={() => handleSave(index)}
                  disabled={savingIndex === index}
                >
                  {savingIndex === index ? (
                    <>
                      <CircularProgress size={16} style={{ color: "inherit" }} />
                      &nbsp;Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </Box>
              <WelcomeUploadDialog
                open={uploadDialog}
                setOpen={setUploadDialog}
                uploadFiles={(files, remainingMediaUrls) =>
                  uploadFiles(files, remainingMediaUrls, index)
                }
                initialMediaUrls={(welcomeMessage.images || []).filter(
                  (item): item is string => typeof item === "string"
                )}
              />
            </Card>
          )
          : (
            <Card key={welcomeMessage._id} className={classes.tierCard} style={{ cursor: "pointer" }} onClick={() => toggleEdit(index, true)}>
              <CardContent className={classes.cardContent}>
                <Box>
                  <Box className={classes.tierHeader}>
                    <Typography className={classes.tierName}>
                      <div>Message {index + 1}</div>
                      <div>
                        {welcomeMessage.plans.map((value) => (
                          <Chip key={value} label={value} className={classes.subscriptionChip} />
                        ))}
                      </div>
                    </Typography>
                  </Box>
                  <Box
                    className={classes.messageText}
                  >{welcomeMessage.text}</Box>
                </Box>
              </CardContent>
            </Card>
          )
      }
      )}
      {!allPlansUsed && <Button
        className={classes.addMoreButton}
        onClick={() => {
          setWelcomeMessages([...welcomeMessages, { _id: new Date().toString(), text: "", plans: [], isEditing: true }]);
        }}
      >
        <Plus /> <span style={{ marginLeft: 4 }}>Add more messages</span>
      </Button>}
    </div >
  );
};

export default WelcomeMessages;
